import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Modal,
  Button,
  TextField,
  Popover,
  Snackbar,
  Alert,
} from "@mui/material";
import React, { useState } from "react";
import {
  deleteBooking,
  updateBooking,
  deletePrice,
} from "../../firebaseConfig";
import { calculateTaxFormatted } from "../../utils/hooks/calculateTax";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import {
  ArrowDownwardOutlined,
  RemoveRedEye,
  Email,
} from "@mui/icons-material";
import { Stack } from "@mui/system";
import calculateNumberOfNights from "../../utils/hooks/calculateNumberOfNights";

type Props = {
  roomDocuments: any[];
  room: string;
  isPrice?: boolean;
  isList?: boolean;
};

const BookingsAtom = ({ roomDocuments, room, isPrice, isList }: Props) => {
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [email, setEmail] = useState("");
  const [price, setPrice] = useState("");
  const [newAdultCount, setNewAdultCount] = useState<number>(0);
  const [newChildrenCount, setNewChildrenCount] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // Stato per il Popover
  const [bookingNumber, setBookingNumber] = useState<string>("");
  const [popoverIndex, setPopoverIndex] = useState<number | null>(null);

  const [isExpanded, setIsExpanded] = useState(false); // Stato per l'accordion
  const [popoverData, setPopoverData] = useState<any>(null);
  const [emailSending, setEmailSending] = useState(false); // Stato per il caricamento del rinvio email
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Stato per il messaggio di successo/errore
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Messaggio del snackbar
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  ); // Tipo di messaggio

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    item: any,
    index: number
  ) => {
    setAnchorEl(event.currentTarget);
    setPopoverData(item);
    setPopoverIndex(index); // Memorizza l'indice della prenotazione selezionata
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverIndex(null);
  };

  const open = Boolean(anchorEl); // Controlla se il Popover deve essere aperto

  function convertTimestampToDate(timestamp: any) {
    return new Date(timestamp.seconds * 1000);
  }

  const handleDeleteClick = async (
    roomId: string,
    startDate: Date,
    endDate: Date,
    isPrice?: boolean
  ) => {
    try {
      setLoading(true);
      await deleteBooking(roomId, startDate, endDate, isPrice);
      window.location.reload();
    } catch (error) {
      console.error("Errore durante la cancellazione:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePrice = async (
    roomId: string,
    startDate: Date,
    endDate: Date,
    isPrice?: boolean
  ) => {
    try {
      setLoading(true);
      await deletePrice(roomId, startDate, endDate);
      window.location.reload();
    } catch (error) {
      console.error("Errore durante la cancellazione:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (booking: any) => {
    const startDate =
      booking.startDate instanceof Date
        ? booking.startDate
        : new Date(booking.startDate.seconds * 1000);

    const endDate =
      booking.endDate instanceof Date
        ? booking.endDate
        : new Date(booking.endDate.seconds * 1000);

    setSelectedBooking(booking);
    setNewStartDate(startDate.toISOString().split("T")[0]);
    setNewEndDate(endDate.toISOString().split("T")[0]);
    setNewAdultCount(booking?.guestInfo?.adultCount || 0);
    setNewChildrenCount(booking?.guestInfo?.childrenCount || 0);
    setPrice(booking?.price.price || "");
    setCustomerName(booking.customerName || "");
    setCustomerPhone(booking.phoneNumber || "");
    setEmail(booking.email || "");
    setBookingNumber(booking.bookingNumber || "");
    setOpenModal(true);
  };
  const handleUpdateBooking = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    if (selectedBooking) {
      try {
        setLoading(true);
        await updateBooking(
          selectedBooking.roomId,
          newStartDate,
          newEndDate,
          newAdultCount,
          newChildrenCount,
          customerPhone,
          customerName,
          email,
          price,
          bookingNumber
        );
        setOpenModal(false);
      } catch (error) {
        console.error("Errore durante l'aggiornamento:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const getRoomTitle = (room: string) => {
    if (room === "1") return "Inno al Re";
    if (room === "2") return "Il Barbiere Di Siviglia";
    if (room === "3") return "L'Osteria Di Marechiaro";
    if (room === "4") return "La Molinara";
    if (room === "5") return "Il Matrimonio Inaspettato";
    return "Titolo Sconosciuto";
  };

  // Funzione per rinviare email di conferma
  const handleResendEmail = async (booking: any) => {
    if (!booking.email) {
      setSnackbarMessage("Email non disponibile per questa prenotazione");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      setEmailSending(true);

      // Prepara i dati della prenotazione per l'email
      const bookingDetails = {
        name: booking.customerName,
        phone: booking.phoneNumber,
        email: booking.email,
        dates: {
          startDate: convertTimestampToDate(booking.startDate).toISOString(),
          endDate: convertTimestampToDate(booking.endDate).toISOString(),
        },
        rooms: [booking.roomId || `room${room}`],
        adultCount: booking.guestInfo?.adultCount || booking.adultsCount || 0,
        childrenCount:
          booking.guestInfo?.childrenCount || booking.childrenCount || 0,
        totalPrice: booking.price?.price || 0,
        tax: calculateTaxFormatted(
          booking.guestInfo?.adultCount || booking.adultsCount || 0,
          convertTimestampToDate(booking.startDate).toISOString(),
          convertTimestampToDate(booking.endDate).toISOString()
        ), // Tassa di soggiorno calcolata correttamente
        paymentMethodUsed: booking.cashInfo?.name ? "cash" : "card",
        lang: "it", // Default italiano
      };

      const response = await fetch(
        "https://residenzastorica1811.vercel.app/resend-confirmation-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: booking.email,
            bookingDetails: bookingDetails,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbarMessage("Email di conferma rinviata con successo!");
        setSnackbarSeverity("success");
      } else {
        setSnackbarMessage(`Errore: ${result.message || "Errore sconosciuto"}`);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("Errore nel rinvio email:", error);
      setSnackbarMessage("Errore di connessione. Riprova più tardi.");
      setSnackbarSeverity("error");
    } finally {
      setEmailSending(false);
      setSnackbarOpen(true);
    }
  };
  return (
    <Box
      sx={{
        width: "100%",
        color: "#fff",
        overflowY: "auto",
      }}
      p={1}
    >
      <Box
        id="roomTitle"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: isList ? "#1A3676FF" : undefined,
          cursor: "pointer",
          p: "10px 10px",
        }}
        onClick={() => setIsExpanded(!isExpanded)} // Toggle dell'accordion
      >
        <Typography
          sx={{
            fontSize: "1.2rem",
            borderBottom: "2px solid #5c5c5c",
            textTransform: "uppercase",
            letterSpacing: 1.5,
            color: isList ? "#fff" : "black",
          }}
        >
          {getRoomTitle(room)}
        </Typography>
        <ArrowDownwardOutlined />
      </Box>

      {isExpanded && (
        <Box width={"100%"}>
          {roomDocuments.map((item, index) => {
            const roomObject = {
              roomId: item.id.split("_")[0],
              startDate: convertTimestampToDate(item.startDate),
              endDate: convertTimestampToDate(item.endDate),
            };

            return (
              <React.Fragment key={index}>
                <Box
                  sx={{
                    width: "100%",
                    p: "10px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginY: 1,
                    backgroundColor: isList ? "#2d2d2d" : undefined,
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="start"
                    justifyContent="space-between"
                    sx={{ borderRadius: 2, gap: 1 }}
                  >
                    <Typography
                      variant="body1"
                      fontSize={20}
                      sx={{ color: isList ? "#fff" : undefined }}
                    >
                      {roomObject.startDate.toLocaleDateString("it-IT", {
                        day: "numeric",
                        month: "short",
                      })}{" "}
                      -{" "}
                      {roomObject.endDate.toLocaleDateString("it-IT", {
                        day: "numeric",
                        month: "short",
                      })}{" "}
                      {item.customerName === "Booking.com" && " - Booking.com"}
                    </Typography>
                    {item.customerName !== "Booking.com" && (
                      <>
                        <Typography
                          variant="body1"
                          fontSize={16}
                          sx={{
                            color: isList ? "#fff" : undefined,
                            fontSize: 20,
                          }}
                        >
                          {item.customerName}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontSize={16}
                          sx={{
                            color: isList ? "#fff" : undefined,
                            fontSize: 20,
                          }}
                        >
                          {item.phoneNumber}
                        </Typography>
                        <Typography
                          sx={{
                            display: isList ? "none" : "block",
                            color: isList ? undefined : "black",
                          }}
                          variant="body1"
                          fontSize={16}
                        >
                          Adulti: {item.guestInfo?.adultCount} <br />
                          Bambini: {item.guestInfo?.childrenCount} <br />
                          Prezzo: {item?.price.price} <br />
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Bottone per aprire il Popover */}

                  {item.customerName !== "Booking.com" && (
                    <Box display="flex" gap={1}>
                      <IconButton
                        onClick={(event) =>
                          handlePopoverOpen(event, item, index)
                        }
                        sx={{ color: "#64ceff" }}
                      >
                        <RemoveRedEye />
                      </IconButton>

                      <IconButton
                        onClick={() => handleEditClick(item)}
                        sx={{ color: "#ff9900" }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() =>
                          isPrice
                            ? handleDeletePrice(
                                roomObject.roomId,
                                roomObject.startDate,
                                roomObject.endDate
                              )
                            : handleDeleteClick(
                                roomObject.roomId,
                                roomObject.startDate,
                                roomObject.endDate
                              )
                        }
                        sx={{ color: "#ff0000" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {/* Componente Popover */}
                <Popover
                  open={Boolean(anchorEl) && popoverIndex === index} // Verifica se il Popover è aperto solo per l'elemento giusto
                  anchorEl={anchorEl}
                  onClose={handlePopoverClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  <Box width={"100vw"} p={2}>
                    {popoverData ? (
                      <>
                        <Typography
                          sx={{
                            fontWeight: 600,
                          }}
                        >
                          DETTAGLI PRENOTAZIONE
                        </Typography>
                        <Typography>
                          Nome: {popoverData.customerName}
                        </Typography>
                        <Typography>
                          Prezzo:{" "}
                          {popoverData.cashInfo && popoverData.cashInfo.name
                            ? popoverData.price.price * 0.9
                            : popoverData.price.price}
                        </Typography>
                        <Typography>
                          Totale persone: {popoverData.adultsCount}
                        </Typography>
                        <Typography>
                          Numero di notti:{" "}
                          {calculateNumberOfNights(
                            convertTimestampToDate(popoverData.startDate),
                            convertTimestampToDate(popoverData.endDate)
                          )}
                        </Typography>

                        <Stack pt={4}>
                          {" "}
                          <Typography
                            variant="body1"
                            sx={{
                              textDecoration: "none",
                              cursor: "pointer",
                            }}
                            component="a"
                            href={`tel:${item.phoneNumber}`}
                          >
                            Telefono: {item.phoneNumber}
                          </Typography>{" "}
                          <Typography
                            variant="body1"
                            sx={{
                              textDecoration: "none",
                              cursor: "pointer",
                            }}
                            component="a"
                            href={`mailto:${item.email}`}
                          >
                            Email: {item.email}
                          </Typography>{" "}
                        </Stack>
                        {item.cashInfo && (
                          <>
                            {item.cashInfo.name ? (
                              <Stack
                                py={2}
                                pl={2}
                                sx={{ backgroundColor: "#f5f5f5" }}
                              >
                                <Typography>
                                  Metodo di pagamento: Cash
                                </Typography>
                                <Typography>
                                  Card Name: {item.cashInfo.name}
                                </Typography>
                                <Typography>
                                  Card Number: {item.cashInfo.cardNumber}
                                </Typography>
                                <Typography>
                                  Expiry Date: {item.cashInfo.expiryDate}
                                </Typography>
                                <Typography>
                                  CVV: {item.cashInfo.cvv}
                                </Typography>
                              </Stack>
                            ) : (
                              <Stack
                                py={2}
                                pl={2}
                                sx={{ backgroundColor: "#f5f5f5" }}
                              >
                                <Typography>
                                  Metodo di pagamento: Card
                                </Typography>
                              </Stack>
                            )}
                          </>
                        )}
                        <Typography>
                          Codice prenotazione:{" "}
                          {popoverData.bookingNumber || "N/A"}
                        </Typography>

                        {/* Pulsante Rinvia Email */}
                        <Box
                          sx={{ mt: 2, pt: 2, borderTop: "1px solid #e0e0e0" }}
                        >
                          <Button
                            variant="contained"
                            startIcon={<Email />}
                            onClick={() => handleResendEmail(popoverData)}
                            disabled={emailSending || !popoverData.email}
                            sx={{
                              backgroundColor: "#4caf50",
                              "&:hover": {
                                backgroundColor: "#45a049",
                              },
                            }}
                          >
                            {emailSending ? (
                              <>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Invio in corso...
                              </>
                            ) : (
                              "Rinvia Email di Conferma"
                            )}
                          </Button>
                          {!popoverData.email && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ display: "block", mt: 1 }}
                            >
                              Email non disponibile per questa prenotazione
                            </Typography>
                          )}
                        </Box>
                        {/* Altri dettagli */}
                      </>
                    ) : (
                      <Typography>Caricamento...</Typography>
                    )}
                  </Box>
                </Popover>
              </React.Fragment>
            );
          })}

          {/* Modal for editing bookings */}
          <Modal
            open={openModal}
            onClose={() => setOpenModal(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box
              sx={{
                width: "100%",
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
              }}
            >
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Modifica Prenotazione
              </Typography>
              <TextField
                label="Nome Cliente"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Telefono"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Data di Inizio"
                type="date"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Data di Fine"
                type="date"
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Prezzo"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Numero Adulti"
                type="number"
                value={newAdultCount}
                onChange={(e) => setNewAdultCount(Number(e.target.value))}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Numero Bambini"
                type="number"
                value={newChildrenCount}
                onChange={(e) => setNewChildrenCount(Number(e.target.value))}
                fullWidth
                sx={{ mb: 2 }}
              />
              <Box display="flex" justifyContent="space-between">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateBooking}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Aggiorna"}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setOpenModal(false)}
                >
                  <CloseIcon />
                </Button>
              </Box>
            </Box>
          </Modal>
        </Box>
      )}
      {loading && <CircularProgress />}

      {/* Snackbar per messaggi di successo/errore */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingsAtom;
